import { useState, useEffect, useRef, useCallback } from 'react';
import config from '../aws-exports.json';
import { fetchAuthSession, getAWSCredentials } from '../network/Auth';
import { signUrl } from '../network/AwsSigner';

const getWebsocketUrl = (name, region = null) => {
  // Use provided region or fall back to the default region from config
  const targetRegion = region || config.region;

  // Get the websocket array for the specified region
  const websockets = config[targetRegion]?.websocket;

  if (!websockets) {
    throw new Error(`No websockets found for region ${targetRegion}`);
  }

  // Find the websocket entry by name
  const websocket = websockets.find(ws => ws.name === name);

  if (!websocket) {
    throw new Error(`WebSocket URL not found for ${name} in region ${targetRegion}`);
  }

  return websocket.endpoint;
};

// WebSocket ready states
export const WS_READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

/**
 * Custom hook to use WebSocket by name instead of URL
 * @param {string} name - The name of the WebSocket (e.g., 'NOTIFICATIONS')
 * @param {object} options - Configuration options
 * @returns {object} - WebSocket utilities and state
 */
export const useNamedWebSocket = (name, options = {}) => {
  const {
    onOpen,
    onClose,
    onError,
    onMessage,
    shouldReconnect = true,
    reconnectAttempts = 10,
    reconnectInterval = 10000,
    enabled = true
  } = options;

  const [readyState, setReadyState] = useState(WS_READY_STATE.CONNECTING);
  const [lastMessage, setLastMessage] = useState(null);

  const wsRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const shouldConnectRef = useRef(enabled);
  const isMountedRef = useRef(true);
  const messageQueue = useRef([]);

  const getSignedUrl = useCallback(async () => {
    const credentials = await getAWSCredentials();
    const session = await fetchAuthSession();

    if (!credentials || !session) {
      throw new Error('User not authenticated');
    }

    /** const accessInfo = {
      access_key: credentials.accessKeyId,
      secret_key: credentials.secretAccessKey,
      session_token: credentials.sessionToken
    }; **/

    const websocketUrl = getWebsocketUrl(name);

    const url = new URL(websocketUrl);
    url.searchParams.append('bs-token', session.idToken.jwtToken);

    const signedUrl = signUrl(url);
    return signedUrl.toString();
  }, [name]);

  const connect = useCallback(async () => {
    if (!shouldConnectRef.current || !isMountedRef.current) {
      return;
    }

    try {
      const url = await getSignedUrl();

      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = (event) => {
        if (!isMountedRef.current) return;

        console.log(`WebSocket [${name}] Connected`, Date.now());
        setReadyState(WS_READY_STATE.OPEN);
        reconnectCountRef.current = 0; // Reset reconnect count on successful connection

        if (onOpen) {
          onOpen(event);
        }
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;

        console.log(`WebSocket [${name}] Disconnected`, Date.now(), event.code, event.reason);
        setReadyState(WS_READY_STATE.CLOSED);

        if (onClose) {
          onClose(event);
        }

        // Attempt to reconnect
        if (shouldReconnect && shouldConnectRef.current) {
          if (reconnectAttempts === Infinity || reconnectCountRef.current < reconnectAttempts) {
            reconnectCountRef.current += 1;
            const delay = typeof reconnectInterval === 'function'
              ? reconnectInterval(reconnectCountRef.current)
              : reconnectInterval;

            console.log(`WebSocket [${name}] Reconnecting attempt ${reconnectCountRef.current} in ${delay}ms`);

            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current && shouldConnectRef.current) {
                setReadyState(WS_READY_STATE.CONNECTING);
                connect();
              }
            }, delay);
          } else {
            console.log(`WebSocket [${name}] Max reconnection attempts reached`);
          }
        }
      };

      ws.onerror = (error) => {
        if (!isMountedRef.current) return;

        console.error(`WebSocket [${name}] Error:`, error);

        if (onError) {
          onError(error);
        }
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;

        console.log(`WebSocket [${name}] Received Message:`, event.data);
        setLastMessage(event);

        if (onMessage) {
          onMessage(event);
        }
      };
    } catch (error) {
      console.error(`WebSocket [${name}] Connection Error:`, error);

      if (onError) {
        onError(error);
      }

      // Retry connection on error
      if (shouldReconnect && shouldConnectRef.current && isMountedRef.current) {
        if (reconnectAttempts === Infinity || reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current += 1;
          const delay = typeof reconnectInterval === 'function'
            ? reconnectInterval(reconnectCountRef.current)
            : reconnectInterval;

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && shouldConnectRef.current) {
              connect();
            }
          }, delay);
        }
      }
    }
  }, [name, getSignedUrl, onOpen, onClose, onError, onMessage, shouldReconnect, reconnectAttempts, reconnectInterval]);

  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message, keep = true) => {
    console.log(`WebSocket [${name}] Sending Message:`, message, wsRef, wsRef?.current);
    if (wsRef.current && wsRef.current.readyState === WS_READY_STATE.OPEN) {
      const data = typeof message === 'string' ? message : JSON.stringify(message);
      wsRef.current.send(data);
      return true;
    } else if (keep) {
      messageQueue.current.push(message);
      return true;
    } else {
      console.warn(`WebSocket [${name}] is not open. Ready state:`, wsRef.current?.readyState);
      return false;
    }
  }, [name]);

  const sendJsonMessage = useCallback((message) => {
    return sendMessage(JSON.stringify(message));
  }, [sendMessage]);

  // Initial connection
  useEffect(() => {
    if (enabled) {
      shouldConnectRef.current = true;
      isMountedRef.current = true;
      connect();
    }

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  useEffect(() => {
    if (readyState === WS_READY_STATE.OPEN) {
      messageQueue.current.forEach((message) => {
        sendMessage(message);
      });
    }
  }, [readyState]);

  return {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    readyState,
    disconnect,
    reconnect: connect
  };
};
